import React, { useState } from "react";
import { Checkbox, FormControlLabel } from "@mui/material";

interface CustomInputProps {
  label: string;
  name: string;
  placeholder?: string;
  type?: string;
  value?: string;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  multiline?: boolean;
  rows?: number;
  error?: string;
  required?: boolean;
  disable?: boolean;
  checkBoxLabel?: string;
  isCheckbox?: boolean;
  checkboxValue?: boolean;
  className?: string;
  onCheckboxChange?: (checked: boolean) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const CustomInput: React.FC<CustomInputProps> = ({
  label,
  name,
  placeholder = "",
  type = "text",
  value = "",
  onChange,
  onBlur,
  multiline = false,
  rows = 4,
  error,
  required = false,
  disable = false,
  isCheckbox = false,
  checkboxValue = false,
  onCheckboxChange,
  checkBoxLabel = "",
  className = "",
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const hasValue = Boolean(value?.trim());

  const baseClass =
    "w-full bg-[#2C293A] text-white px-4 py-2 outline-none transition-all duration-200";

  const borderColor = error
    ? "border border-red-500"
    : isFocused || isHovered || hasValue
      ? "border border-[var(--color-stat-accent)]"
      : "border border-[var(--color-border)]";

  const inputClass = `${baseClass} ${
    multiline ? "rounded-xl" : "rounded-full"
  } ${borderColor}`;

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={name} className="block text-sm font-medium text-white">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>

        {isCheckbox && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={checkboxValue}
                onChange={(e) => onCheckboxChange?.(e.target.checked)}
                sx={{
                  transform: "scale(0.85)",
                  color: "var(--color-stat-accent)",
                  "&.Mui-checked": { color: "var(--color-stat-accent)" },
                  p: 0.5,
                }}
              />
            }
            label={
              <span style={{ fontSize: "0.85rem", color: "var(--color-stat-accent)" }}>
                {checkBoxLabel}
              </span>
            }
            sx={{ m: 0 }}
          />
        )}
      </div>

      {multiline ? (
        <textarea
          id={name}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange?.(e)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          rows={rows}
          className={inputClass}
          disabled={disable}
        />
      ) : (
        <input
          id={name}
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange?.(e)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={inputClass}
          disabled={disable}
        />
      )}
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default CustomInput;
